import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink } from "components";
import { useAnchor } from "hooks";
import { CheckIcon } from "icons";
import { Pricing3Content } from "types/graphql/generated";

interface Props {
  content: Pricing3Content;
}

const Pricing3: VFC<Props> = withErrorBoundary(
  ({
    content: {
      titleBottom,
      titleTop,
      pricing3ListRight,
      pricing3ListLeft,
      pricing3Link,
      pricing3LinkLabel,
      description,
      pricing3DisclaimerTitle,
      pricing3DisclaimerDescription,
      descriptionNote,
    },
  }) => {
    useAnchor("pricing3");

    return (
      <StyledPricing3 className="pricing3 Content">
        <div className="Pricing3__topContainerWrapper">
          <div className="Pricing3__topContainer">
            <h2 className="Pricing3__containerTitle Font_24_32_600">{titleTop}</h2>

            <div className="Pricing3__topContainerContent">
              <Markdown className="Pricing3__price">{description}</Markdown>

              <Markdown className="Pricing3__priceNote Color_secondary Font_18_26_450">{descriptionNote}</Markdown>

              <ArrowLink href={pricing3Link} className="Pricing3__priceArrowLink Color_dusk50 Font_18_26_600">
                {pricing3LinkLabel}
              </ArrowLink>
            </div>
          </div>

          {pricing3DisclaimerTitle && pricing3DisclaimerDescription && (
            <div className="Pricing3__disclaimer">
              <p className="Pricing3__disclaimerTitle Font_18_26_600">{pricing3DisclaimerTitle}</p>
              <Markdown className="Pricing3__disclaimerDescription Font_18_26_450">
                {pricing3DisclaimerDescription}
              </Markdown>
            </div>
          )}
        </div>

        <div className="Pricing3__bottomContainer">
          <h2 className="Pricing3__containerTitle Font_24_32_600">{titleBottom}</h2>

          <div className="Pricing3__bottomContainerContent">
            <ul className="Pricing3__list">
              {pricing3ListLeft.map(({ description, title, id }) => (
                <li key={id} className="Pricing3__listItem">
                  <CheckIcon className="Pricing3__checkIcon" />

                  <div className="Pricing3__listItemContent">
                    <Markdown className="Font_18_26_700">{title}</Markdown>

                    <Markdown className="Pricing3__listItemContentDescription Color_tertiary Font_16_22_450">
                      {description}
                    </Markdown>
                  </div>
                </li>
              ))}
            </ul>

            <ul className="Pricing3__list">
              {pricing3ListRight.map(({ description, title, id }) => (
                <li key={id} className="Pricing3__listItem">
                  <CheckIcon className="Pricing3__checkIcon" />

                  <div className="Pricing3__listItemContent">
                    <Markdown className="Font_18_26_700">{title}</Markdown>

                    <Markdown className="Pricing3__listItemContentDescription Color_tertiary Font_16_22_450">
                      {description}
                    </Markdown>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </StyledPricing3>
    );
  },
  {}
);

const StyledPricing3 = styled.section`
  .cost {
    display: flex;
    align-items: baseline;
    color: ${({ theme }) => theme.colors.fog["80"]};
  }

  .price {
    font-size: 48px;
    line-height: 54px;
    font-weight: 600;
    letter-spacing: -0.02em;
  }

  .month {
    margin-left: 2px;
    font-size: 18px;
    line-height: 26px;
    font-weight: 700;
    letter-spacing: 0.004em;
  }

  .plus {
    margin-left: 24px;
    margin-right: 24px;
    font-size: 48px;
    line-height: 54px;
    font-weight: 600;
    letter-spacing: -0.02em;
  }

  .Pricing3__containerTitle {
    max-width: 200px;
    width: 100%;
  }

  .Pricing3__topContainerWrapper {
    padding-bottom: 48px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["20"]};
  }

  .Pricing3__topContainer {
    display: flex;

    .Pricing3__containerTitle {
      margin-top: 19px;
    }
  }

  .Pricing3__topContainerContent {
    margin-left: 72px;
  }

  .Pricing3__price {
    display: flex;
  }

  .Pricing3__priceNote {
    margin-top: 16px;
  }

  .Pricing3__priceArrowLink {
    margin-top: 24px;

    svg {
      width: 12px;
      height: 12px;
      fill: ${({ theme }) => theme.colors.dusk["50"]};
    }

    &:hover {
      opacity: 0.7;
    }
  }

  .Pricing3__bottomContainer {
    padding-top: 48px;
    display: flex;
  }

  .Pricing3__bottomContainerContent {
    display: flex;
    margin-left: 72px;
  }

  .Pricing3__checkIcon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin: 6px 6px 6px 0;
    stroke: ${({ theme }) => theme.colors.dusk["50"]};
  }

  .Pricing3__list {
    &:not(:first-child) {
      margin-left: 64px;
    }
  }

  .Pricing3__listItem {
    display: flex;
    max-width: 393px;

    &:not(:first-child) {
      margin-top: 32px;
    }
  }

  .Pricing3__listItemContentDescription {
    margin-top: 4px;
  }

  .Pricing3__listItemContent {
    margin-left: 8px;
  }

  .Pricing3__disclaimer {
    display: flex;
    margin-top: 48px;
    padding: 24px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["30"]};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.fog["05"]};
  }

  .Pricing3__disclaimerTitle {
    flex-shrink: 0;
  }

  .Pricing3__disclaimerDescription {
    width: 674px;
    max-width: 100%;
    margin-left: 16px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Pricing3__bottomContainerContent {
      margin-left: 48px;
    }

    .Pricing3__containerTitle {
      max-width: 230px;
    }

    .Pricing3__disclaimerDescription {
      width: 363px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Pricing3__topContainerWrapper {
      padding-bottom: 40px;
    }

    .Pricing3__containerTitle {
      max-width: 100%;
    }

    .Pricing3__topContainer {
      flex-direction: column;

      .Pricing3__containerTitle {
        margin-top: unset;
      }
    }

    .Pricing3__topContainerContent {
      margin-left: unset;
      margin-top: 24px;
    }

    .Pricing3__bottomContainer {
      flex-direction: column;
      padding-top: 40px;
    }

    .Pricing3__bottomContainerContent {
      margin-left: unset;
      margin-top: 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Pricing3__bottomContainerContent {
      flex-direction: column;
    }

    .Pricing3__list {
      &:not(:first-child) {
        margin-left: unset;
        margin-top: 24px;
      }
    }

    .Pricing3__listItem {
      max-width: 100%;
    }

    .Pricing3__topContainerWrapper {
      padding-bottom: 32px;
    }

    .Pricing3__topContainerContent {
      margin-top: 16px;
    }

    .Pricing3__bottomContainer {
      padding-top: 32px;
    }

    .Pricing3__disclaimer {
      flex-direction: column;
      margin-top: 32px;
    }

    .Pricing3__disclaimerDescription {
      margin-left: 0;
      width: 100%;
      margin-top: 16px;
    }

    .Pricing3__price {
      flex-direction: column;
    }

    .plus {
      margin-top: 8px;
      margin-left: unset;
      margin-right: unset;
    }

    .cost:not(:first-child) {
      margin-top: 8px;
    }
  }
`;

gql`
  fragment Pricing3Content on ComponentPricingPricing3 {
    titleTop
    description
    pricing3ListLeft {
      id
      title
      description
    }
    pricing3ListRight {
      id
      title
      description
    }
    pricing3Link
    pricing3LinkLabel
    titleBottom
    pricing3DisclaimerTitle
    pricing3DisclaimerDescription
    descriptionNote
  }
`;

export { Pricing3 };
export type { Props as Pricing3Props };
